import React, { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  adminContent,
  type RoleplayPerformanceBreakdownMetric,
  type RoleplayPerformanceEvent,
  type RoleplayPerformanceGroup,
  type RoleplayPerformanceReport,
  type RoleplayPerformanceStat,
  type RoleplayPerformanceWindow,
} from 'services/api/adminContentClient';
import RoleplayPerformanceTrends, { type RoleplayTrendSelection } from './RoleplayPerformanceTrends';

const duration = (value: number | null | undefined) => value == null ? 'Unavailable' : `${(value / 1000).toFixed(2)} s`;
const metric = (value: number | null, unit: string) => value == null ? 'Unavailable' : `${value.toFixed(1)} ${unit}`;
const emptyStat: RoleplayPerformanceStat = { count: 0, p50_ms: null, p95_ms: null };
const speechMetrics: ReadonlyArray<readonly [RoleplayPerformanceBreakdownMetric, string]> = [
  ['local_speech_stop_to_audio_ms', 'Estimated local speech end → first audio'],
  ['speech_stop_to_audio_ms', 'Provider speech-stop received → first audio'],
  ['local_speech_stop_to_provider_ms', 'Extra wait before provider event (paired difference)'],
];
const elevenLabsMetrics: ReadonlyArray<readonly [RoleplayPerformanceBreakdownMetric, string]> = [
  ['elevenlabs_connection_ms', 'ElevenLabs connection setup'],
  ['elevenlabs_text_buffer_ms', 'First text → first text sent to ElevenLabs'],
  ['elevenlabs_first_audio_ms', 'Text sent → first ElevenLabs audio received'],
  ['elevenlabs_audio_to_playback_ms', 'First ElevenLabs audio received → playback'],
  ['elevenlabs_max_audio_gap_ms', 'Largest gap between ElevenLabs audio chunks'],
];
const breakdownLabels = (group: { input_mode: string; provider: string }) => [
  ...(group.input_mode === 'voice' ? speechMetrics : []),
  ...(group.provider === 'elevenlabs' ? elevenLabsMetrics : []),
];

const Percentiles = ({ stat }: { stat: RoleplayPerformanceStat }) => (
  <>
    <div className="whitespace-nowrap">{duration(stat.p50_ms)} / {duration(stat.p95_ms)}</div>
    <div className="text-xs text-muted-foreground">{stat.count} samples</div>
  </>
);

const VoiceBreakdown = ({ group }: { group: RoleplayPerformanceGroup }) => (
  <details className="rounded-xl border border-border/60 p-3">
    <summary className="cursor-pointer break-words text-sm">
      {group.provider} · {group.model ?? 'Unavailable'} · {group.input_mode} input · {group.count} attempts
    </summary>
    <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
      {breakdownLabels(group).map(([key, label]) => <div key={key}>
        <dt className="text-muted-foreground">{label}</dt>
        <dd><Percentiles stat={group.breakdown?.[key] ?? emptyStat} /></dd>
      </div>)}
    </dl>
  </details>
);

const RecentTurn = ({ event }: { event: RoleplayPerformanceEvent }) => (
  <details className="rounded-xl border border-border/60 p-3">
    <summary className="cursor-pointer text-sm">
      Session {event.session_id} · {event.mode} / {event.input_mode} input · {event.provider} · {event.outcome}
      {' '}· first response {duration(event.first_response_ms)}
    </summary>
    <dl className="mt-3 grid gap-3 break-words text-sm sm:grid-cols-2 lg:grid-cols-3">
      {([
        ['Recorded', new Date(event.recorded_at).toLocaleString()],
        ['Event / turn / generation', `${event.id} / ${event.turn_id ?? 'Unavailable'} / ${event.generation ?? 'Unavailable'}`],
        ['Model', event.model ?? 'Unavailable'],
        ['Voice', event.voice ?? 'Unavailable'],
        ['App version', event.app_version ?? 'Unavailable'],
        ['Error code', event.error_code ?? 'None'],
        ['Browser controls ready', duration(event.controls_ready_ms)],
        ['Browser backend decision wait', duration(event.backend_decision_ms)],
        ['Server evaluation', duration(event.server_evaluation_ms)],
        ['Server pre / post evaluation', `${duration(event.server_pre_evaluation_ms)} / ${duration(event.server_post_evaluation_ms)}`],
        ['Server actor generation', duration(event.server_generation_ms)],
        ['OpenAI WebRTC round-trip time', metric(event.network.rtt_ms, 'ms')],
        ['OpenAI WebRTC audio jitter', metric(event.network.jitter_ms, 'ms')],
        ['OpenAI WebRTC packet loss', metric(event.network.packet_loss_percent, '%')],
        ...(event.mode === 'voice' ? breakdownLabels(event).map(([key, label]) =>
          [label, duration(event.breakdown?.[key])] as const) : []),
      ] as const).map(([label, value]) => (
        <div key={label}><dt className="text-muted-foreground">{label}</dt><dd>{value}</dd></div>
      ))}
    </dl>
  </details>
);

const RoleplayPerformanceCard: React.FC = () => {
  const [window, setWindow] = useState<RoleplayPerformanceWindow>('24h');
  const [refresh, setRefresh] = useState(0);
  const [report, setReport] = useState<RoleplayPerformanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [trendSelection, setTrendSelection] = useState<RoleplayTrendSelection>({ group: '', metric: 'first_response_ms' });

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    setReport(null);
    adminContent.operations.getRoleplayPerformance(window, controller.signal)
      .then(({ data }) => { if (!controller.signal.aborted) setReport(data); })
      .catch(() => { if (!controller.signal.aborted) setFailed(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [window, refresh]);

  return (
    <Card className="w-full min-w-0 max-w-none" maxWidth="none" data-testid="roleplay-performance-card">
      <CardHeader><CardTitle><h2>Roleplay Performance</h2></CardTitle></CardHeader>
      <CardContent className="items-stretch space-y-3">
        <p className="text-sm text-muted-foreground">
          Browser wait times and server AI timings from recorded turns. No conversation text is included.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Performance time window" className="flex flex-wrap gap-2">
            {([['24h', '24 hours'], ['7d', '7 days'], ['30d', '30 days']] as const).map(([value, label]) => (
              <Button key={value} size="auto" variant={window === value ? 'default' : 'secondary'}
                aria-pressed={window === value} onClick={() => setWindow(value)}>{label}</Button>
            ))}
          </div>
          <Button size="auto" variant="secondary" disabled={loading} onClick={() => setRefresh((value) => value + 1)}>
            Refresh performance
          </Button>
        </div>
        {loading && <p role="status" className="text-sm">Loading roleplay performance...</p>}
        {failed && <p role="alert" className="text-sm">Roleplay performance could not be loaded. Try refreshing.</p>}
        {report && <>
          <p className="text-sm">
            {report.total_turns} recorded turn attempts · {report.failed_turns} errors · retained for {report.retention_days} days.
            {' '}Updated {new Date(report.generated_at).toLocaleString()}.
          </p>
          {report.truncated && <p role="status" className="text-sm font-medium">
            Sample limit reached: this report includes only the latest {report.sample_limit.toLocaleString()} turn attempts in this window.
          </p>}
          {report.groups.length === 0 ? <p className="text-sm">No roleplay performance records in this window yet.</p> : <>
            <RoleplayPerformanceTrends report={report} selection={trendSelection} onSelectionChange={setTrendSelection} />
            <h3 className="pt-2 text-base font-semibold">Summary by mode and provider</h3>
            <p className="text-xs text-muted-foreground">
              Each timing shows p50 (median) / p95, with its own sample count. Missing measurements are unavailable, not zero.
            </p>
            <Table aria-label="Roleplay latency by mode and provider">
              <TableHeader><TableRow>
                <TableHead>Mode / input</TableHead><TableHead>Provider / model</TableHead><TableHead>Attempts / errors</TableHead>
                <TableHead>Browser first response</TableHead><TableHead>Browser controls ready</TableHead>
                <TableHead>Server evaluation</TableHead><TableHead>Server actor generation</TableHead>
              </TableRow></TableHeader>
              <TableBody>{report.groups.map((group) => <TableRow key={JSON.stringify([group.mode, group.input_mode, group.provider, group.model])}>
                <TableCell className="whitespace-nowrap">{group.mode} / {group.input_mode}</TableCell>
                <TableCell><div>{group.provider}</div><div className="text-xs">{group.model ?? 'Unavailable'}</div></TableCell>
                <TableCell>{group.count} / {group.error_count}</TableCell>
                <TableCell><Percentiles stat={group.metrics.first_response_ms} /></TableCell>
                <TableCell><Percentiles stat={group.metrics.controls_ready_ms} /></TableCell>
                <TableCell><Percentiles stat={group.metrics.server_evaluation_ms} /></TableCell>
                <TableCell><Percentiles stat={group.metrics.server_generation_ms} /></TableCell>
              </TableRow>)}</TableBody>
            </Table>
            {report.groups.some((group) => group.mode === 'voice') && <section aria-label="Voice timing breakdown" className="space-y-2">
              <h3 className="text-sm font-semibold">Voice timing breakdown</h3>
              <p className="text-xs text-muted-foreground">
                Expand a group for p50 / p95 and sample counts. The extra wait is measured per attempt;
                it is not the difference between group medians.
              </p>
              {report.groups.filter((group) => group.mode === 'voice').map((group) =>
                <VoiceBreakdown key={JSON.stringify([group.input_mode, group.provider, group.model])} group={group} />)}
            </section>}
          </>}
          {report.recent_events.length > 0 && <div className="space-y-2">
            <h3 className="text-sm font-semibold">Recent slow or failed turn attempts</h3>
            <p className="text-xs text-muted-foreground">
              Up to 25 attempts with errors or first-response waits of at least {duration(report.slow_threshold_ms)}; otherwise the latest attempts.
              Expand an attempt for timings and connection details.
            </p>
            {report.recent_events.map((event) => <RecentTurn key={event.id} event={event} />)}
          </div>}
        </>}
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer font-medium">How to read these timings</summary>
          <div className="mt-2 space-y-2">
            <p>First-response timings cover reply attempts, excluding the initial voice connection and opening.
              The breakdown separately includes setup time for the active ElevenLabs connection, reused across turns.
              A retry may record another attempt for the same learner turn.</p>
            <p>Text first response measures submit to visible reply. Voice with typed input measures submit to first audio.
              Spoken Voice starts when the browser receives the provider’s speech-stopped event and ends at first audio;
              speech detection and network delay before that event are excluded from the main table.</p>
            <p>The Voice breakdown also measures from an estimated local speech end using microphone audio energy.
              This is not an exact physical speech boundary. Its extra wait to the provider event includes speech detection,
              transport and browser scheduling; it is not a pure network measurement. Missing or unreliable estimates are unavailable.</p>
            <p>Server timings measure AI API work. Evaluation includes pre- and post-response checks; server phases can overlap
              and must not be added together as the total wait. Server totals include all logged calls, including retries, for the matched learner turn;
              they are not durations for an individual upload or request. Browser-based generation may have no server generation measurement.</p>
            <p>Connection samples cover the OpenAI WebRTC connection only. They do not measure the ElevenLabs WebSocket.
              Missing connection data cannot establish that the connection was healthy.</p>
            <p>ElevenLabs text sent to first audio combines synthesis and network time. Audio received to playback includes
              browser buffering and the playback gate. Connection setup is connection-level context, not an added delay on each turn.
              The largest audio-chunk gap can include provider pacing and browser scheduling,
              and is not proof of a network stall. These phases can overlap and should not be added as total wait.</p>
            <p>Collection is best effort: disconnected clients may not upload records. Percentiles describe recorded samples,
              including partial turns where a timing is available, and small samples may vary substantially.</p>
          </div>
        </details>
      </CardContent>
    </Card>
  );
};

export default RoleplayPerformanceCard;
